import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Keeping a page in step with the notification bell.
 *
 * The bell polls; the pages under it do not. A teacher sitting on Leave
 * Requests saw the badge count a new request and the list beside it stay as it
 * was until a manual refresh. The bell already knows what arrived and where it
 * points, so it says so, and a page showing that address reads again.
 *
 * A window event rather than a store: the bell and the page share no parent
 * below the router, and nothing here needs to outlive the tab.
 */

const ARRIVED = 'notifications:arrived';

/** Called by the bell with the links of notifications it has not seen before. */
export function announceNotificationLinks(links: string[]) {
  if (links.length === 0) return;
  window.dispatchEvent(new CustomEvent<string[]>(ARRIVED, { detail: links }));
}

/**
 * A value to put in a `useApiQuery` dependency list so the read runs again on
 * every visit to this address — including a notification link to the page
 * already open, which still pushes a new entry — and whenever a new
 * notification arrives pointing at it.
 */
export function useRefreshKey(): string {
  const { key, pathname } = useLocation();
  const [arrivals, setArrivals] = useState(0);

  useEffect(() => {
    function onArrived(event: Event) {
      const links = (event as CustomEvent<string[]>).detail;
      if (links.includes(pathname)) setArrivals((count) => count + 1);
    }

    window.addEventListener(ARRIVED, onArrived);
    return () => window.removeEventListener(ARRIVED, onArrived);
  }, [pathname]);

  return `${key}:${arrivals}`;
}
